import React, { useEffect, useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RotateCcw, ExternalLink, User, Building2, DollarSign, Lock, FileText, Users, TrendingUp, Calendar, CheckCircle, Upload } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
    Box,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Text,
    VStack,
    HStack,
    Divider,
    SimpleGrid,
    Badge,
    useColorModeValue,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Alert,
    AlertIcon,
    Skeleton,
    SkeletonText,
    IconButton,
    Icon,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Code,
    Link,
    Button,
} from '@chakra-ui/react';

const UwmLoanDetailsDrawer = ({ isOpen, onClose, loan }) => {
    const { SuperAdmin: isSuperAdmin } = useAuth();
    const [uwmData, setUwmData] = useState(null);
    const [rawData, setRawData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const bgColor = useColorModeValue('gray.50', 'gray.800');
    const headerColor = useColorModeValue('gray.900', 'white');
    const subtextColor = useColorModeValue('gray.600', 'gray.400');
    const cardBg = useColorModeValue('white', 'gray.700');
    const cardBorder = useColorModeValue('gray.200', 'gray.600');
    const accordionButtonBg = useColorModeValue('gray.100', 'gray.700');
    const codeBg = useColorModeValue('gray.50', 'gray.900');
    
    // Rate Lock colors for header
    const rateLockBgNormal = useColorModeValue('blue.50', 'blue.900');
    const rateLockBgExpired = useColorModeValue('red.50', 'red.900');
    const rateLockBorderNormal = useColorModeValue('blue.200', 'blue.700');
    const rateLockBorderExpired = useColorModeValue('red.200', 'red.700');
    
    // Cleared condition background
    const clearedConditionBg = useColorModeValue('green.50', 'green.900');

    useEffect(() => {
        console.log('UwmLoanDetailsDrawer mounted. Is Super Admin?', isSuperAdmin);
    }, [isSuperAdmin]);

    const fetchUwmData = useCallback(async () => {
        if (!loan?.id) return;

        setIsLoading(true);
        setError(null);
        try {
            const functions = getFunctions();
            const getUwmLoanData = httpsCallable(functions, 'getUwmLoanData');
            console.log('Fetching UWM data for loan ID:', loan.id);
            const result = await getUwmLoanData({ loanId: loan.id });
            console.log('UWM API Response:', result.data);
            console.log('Is SuperAdmin:', isSuperAdmin);
            console.log('Has rawData:', !!result.data.rawData);
            setUwmData(result.data.formattedData);
            setRawData(result.data.rawData);
            console.log('State after setting - uwmData:', !!result.data.formattedData, 'rawData:', !!result.data.rawData);
        } catch (err) {
            console.error("Error fetching UWM data:", err);
            console.error("Error code:", err.code);
            console.error("Error message:", err.message);
            console.error("Error details:", err.details);
            
            // Provide more specific error messages based on error code
            let errorMessage = "Could not load loan data from UWM. ";
            
            if (err.code === 'unauthenticated') {
                errorMessage += "Authentication failed. Please check API credentials.";
            } else if (err.code === 'not-found') {
                errorMessage += `Loan ID "${loan.id}" not found in UWM system.`;
            } else if (err.code === 'invalid-argument') {
                errorMessage += "Invalid loan ID provided.";
            } else if (err.code === 'resource-exhausted') {
                errorMessage += "Rate limit exceeded. Please try again in a moment.";
            } else if (err.code === 'failed-precondition') {
                errorMessage += "UWM API credentials not configured on server.";
            } else {
                errorMessage += err.message || "Please try again later.";
            }
            
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [loan, isSuperAdmin]);

    useEffect(() => {
        if (isOpen) {
            fetchUwmData();
        }
    }, [isOpen, fetchUwmData]);

    const InfoCard = ({ title, data, isLoading, expectedKeys }) => (
        <Box p={5} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder}>
            <Text fontSize="lg" fontWeight="bold" mb={3} color={headerColor}>{title}</Text>
            <VStack align="stretch" spacing={3}>
                {expectedKeys.map((key) => (
                    <HStack key={key} justify="space-between">
                        <Text fontSize="sm" color={subtextColor} textTransform="capitalize" whiteSpace="nowrap">{key.replace(/([A-Z])/g, ' $1')}</Text>
                        <Skeleton isLoaded={!isLoading} w="50%" h="20px" borderRadius="md">
                            <Text fontSize="xs" fontWeight="medium" color={headerColor} textAlign="right">
                                {data?.[key] || 'N/A'}
                            </Text>
                        </Skeleton>
                    </HStack>
                ))}
            </VStack>
        </Box>
    );

    const DataRow = ({ label, value, valueColor }) => (
        <HStack justify="space-between" py={1}>
            <Text fontSize="sm" color={subtextColor}>{label}</Text>
            <Text fontSize="xs" fontWeight="medium" color={valueColor || headerColor}>{value || 'N/A'}</Text>
        </HStack>
    );

    const StatusBadge = ({ isActive, activeText = "Active", inactiveText = "Inactive" }) => (
        <Badge colorScheme={isActive ? "green" : "gray"} variant="subtle">
            {isActive ? activeText : inactiveText}
        </Badge>
    );

    // Helper function to get category badge color
    const getCategoryColor = (category) => {
        const categoryColors = {
            'Assets': 'blue',
            'Invoice': 'orange',
            'Appraisal': 'purple',
            'Income': 'green',
            'Credit': 'cyan',
            'Title': 'pink',
            'Insurance': 'teal',
            'Compliance': 'red',
        };
        return categoryColors[category] || 'gray';
    };

    const ConditionsCard = ({ conditions, isLoading }) => {
        const hoverBg = useColorModeValue('gray.100', 'gray.600');
        
        // Calculate total open conditions across all groups
        const totalOpenCount = isLoading ? '...' : 
            (conditions?.groups || []).reduce((sum, group) => sum + (group?.conditions?.length || 0), 0);
        const clearedCount = isLoading ? '...' : conditions?.cleared?.length || 0;

        return (
            <Tabs isFitted variant="soft-rounded">
                <TabList mb="1em">
                    <Tab>Open <Badge ml={1} colorScheme="red">{totalOpenCount}</Badge></Tab>
                    <Tab>Cleared <Badge ml={1} colorScheme="green">{clearedCount}</Badge></Tab>
                </TabList>
                <TabPanels>
                    {/* Open Conditions - Grouped by Processing Stage */}
                    <TabPanel p={0}>
                        {isLoading && <SkeletonText mt="4" noOfLines={5} spacing="4" skeletonHeight="2" />}
                        {!isLoading && conditions?.groups && conditions.groups.length > 0 ? (
                            <Accordion allowMultiple>
                                {conditions.groups.map((group, groupIndex) => (
                                    <AccordionItem key={groupIndex} borderWidth="1px" borderRadius="md" mb={2} borderColor={cardBorder}>
                                        <h2>
                                            <AccordionButton _expanded={{ bg: accordionButtonBg }}>
                                                <HStack flex="1" textAlign="left" spacing={2}>
                                                    <Text fontSize="sm" fontWeight="semibold" color={headerColor}>
                                                        {group.key}
                                                    </Text>
                                                    <Badge colorScheme="red" fontSize="xs">
                                                        {group.conditions?.length || 0}
                                                    </Badge>
                                                </HStack>
                                                <AccordionIcon />
                                            </AccordionButton>
                                        </h2>
                                        <AccordionPanel pb={3}>
                                            <VStack align="stretch" spacing={2}>
                                                {group.conditions?.map((cond, condIndex) => (
                                                    <Box 
                                                        key={cond.id || condIndex}
                                                        p={3}
                                                        borderWidth="1px"
                                                        borderColor={cardBorder}
                                                        borderRadius="md"
                                                        _hover={{ bg: hoverBg }}
                                                    >
                                                        <HStack justify="space-between" align="start" mb={2}>
                                                            <Badge 
                                                                colorScheme={getCategoryColor(cond.category)} 
                                                                fontSize="xs"
                                                                px={2}
                                                                py={0.5}
                                                            >
                                                                {cond.category}
                                                            </Badge>
                                                            <HStack spacing={1}>
                                                                <Icon 
                                                                    as={cond.isUploaded ? CheckCircle : Upload} 
                                                                    boxSize={3.5}
                                                                    color={cond.isUploaded ? "green.500" : "gray.400"}
                                                                />
                                                                <Text fontSize="xs" color={subtextColor}>
                                                                    {cond.isUploaded ? 'Uploaded' : 'Pending'}
                                                                </Text>
                                                            </HStack>
                                                        </HStack>
                                                        <Text fontSize="xs" color={headerColor} lineHeight="1.4">
                                                            {cond.description}
                                                        </Text>
                                                    </Box>
                                                ))}
                                            </VStack>
                                        </AccordionPanel>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        ) : (
                            !isLoading && <Text p={2} fontSize="sm" color={subtextColor}>No open conditions.</Text>
                        )}
                    </TabPanel>

                    {/* Cleared Conditions */}
                    <TabPanel p={0}>
                        {isLoading && <SkeletonText mt="4" noOfLines={4} spacing="4" skeletonHeight="2" />}
                        {!isLoading && conditions?.cleared && conditions.cleared.length > 0 ? (
                            <VStack align="stretch" spacing={2}>
                                {conditions.cleared.map((cond, index) => (
                                    <Box 
                                        key={cond.id || index}
                                        p={3}
                                        borderWidth="1px"
                                        borderColor={cardBorder}
                                        borderRadius="md"
                                        _hover={{ bg: hoverBg }}
                                        bg={clearedConditionBg}
                                    >
                                        <HStack justify="space-between" align="start" mb={2}>
                                            <HStack spacing={2}>
                                                <Icon as={CheckCircle} boxSize={4} color="green.500" />
                                                <Badge 
                                                    colorScheme={getCategoryColor(cond.category)} 
                                                    fontSize="xs"
                                                    px={2}
                                                    py={0.5}
                                                >
                                                    {cond.category}
                                                </Badge>
                                            </HStack>
                                            {cond.satisfactionDate && (
                                                <Text fontSize="xs" color={subtextColor}>
                                                    {new Date(cond.satisfactionDate).toLocaleDateString()}
                                                </Text>
                                            )}
                                        </HStack>
                                        <Text fontSize="xs" color={headerColor} lineHeight="1.4">
                                            {cond.description}
                                        </Text>
                                    </Box>
                                ))}
                            </VStack>
                        ) : (
                            !isLoading && <Text p={2} fontSize="sm" color={subtextColor}>No cleared conditions.</Text>
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>
        );
    };

    const RealtorCard = ({ title, agent, icon, colorScheme, isLoading }) => {
        const hasData = agent && Object.values(agent).some(val => val !== null && val !== undefined);
        
        if (!isLoading && !hasData) return null;

        return (
            <Box p={5} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder}>
                <HStack mb={3}>
                    <Icon as={icon} boxSize={5} color={`${colorScheme}.500`} />
                    <Text fontSize="lg" fontWeight="bold" color={headerColor}>{title}</Text>
                </HStack>
                <VStack align="stretch" spacing={3}>
                    {isLoading ? (
                        <SkeletonText noOfLines={6} spacing="3" skeletonHeight="2" />
                    ) : (
                        <>
                            {agent?.name && (
                                <Box>
                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold" textTransform="uppercase">Agent Name</Text>
                                    <Text fontSize="xs" fontWeight="medium" color={headerColor}>{agent.name}</Text>
                                </Box>
                            )}
                            {agent?.email && (
                                <Box>
                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold" textTransform="uppercase">Email</Text>
                                    <Link href={`mailto:${agent.email}`} fontSize="xs" color="blue.500" _hover={{ textDecoration: 'underline' }}>
                                        {agent.email}
                                    </Link>
                                </Box>
                            )}
                            {agent?.phone && (
                                <Box>
                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold" textTransform="uppercase">Phone</Text>
                                    <Link href={`tel:${agent.phone}`} fontSize="xs" color="blue.500" _hover={{ textDecoration: 'underline' }}>
                                        {agent.phone}
                                    </Link>
                                </Box>
                            )}
                            {(agent?.license || agent?.nmls) && (
                                <HStack spacing={4}>
                                    {agent?.license && (
                                        <Box>
                                            <Text fontSize="xs" color={subtextColor} fontWeight="semibold" textTransform="uppercase">License</Text>
                                            <Badge colorScheme={colorScheme} variant="subtle">{agent.license}</Badge>
                                        </Box>
                                    )}
                                    {agent?.nmls && (
                                        <Box>
                                            <Text fontSize="xs" color={subtextColor} fontWeight="semibold" textTransform="uppercase">NMLS</Text>
                                            <Badge colorScheme={colorScheme} variant="subtle">{agent.nmls}</Badge>
                                        </Box>
                                    )}
                                </HStack>
                            )}
                            {agent?.agency && (
                                <>
                                    <Divider />
                                    <Box>
                                        <HStack mb={2}>
                                            <Icon as={Building2} boxSize={4} color={`${colorScheme}.400`} />
                                            <Text fontSize="xs" color={subtextColor} fontWeight="semibold" textTransform="uppercase">Agency</Text>
                                        </HStack>
                                        <Text fontSize="xs" fontWeight="medium" color={headerColor}>{agent.agency}</Text>
                                    </Box>
                                    {(agent?.agencyLicense || agent?.agencyNMLS) && (
                                        <HStack spacing={4}>
                                            {agent?.agencyLicense && (
                                                <Box>
                                                    <Text fontSize="xs" color={subtextColor}>License</Text>
                                                    <Badge colorScheme={colorScheme} variant="outline" size="sm">{agent.agencyLicense}</Badge>
                                                </Box>
                                            )}
                                            {agent?.agencyNMLS && (
                                                <Box>
                                                    <Text fontSize="xs" color={subtextColor}>NMLS</Text>
                                                    <Badge colorScheme={colorScheme} variant="outline" size="sm">{agent.agencyNMLS}</Badge>
                                                </Box>
                                            )}
                                        </HStack>
                                    )}
                                    {(agent?.address || agent?.city || agent?.state || agent?.zipCode) && (
                                        <Box>
                                            <Text fontSize="xs" color={subtextColor} fontWeight="semibold" textTransform="uppercase">Address</Text>
                                            <Text fontSize="xs" color={headerColor}>
                                                {agent.address && `${agent.address}`}
                                                {(agent.city || agent.state || agent.zipCode) && (
                                                    <>
                                                        {agent.address && <br />}
                                                        {agent.city && `${agent.city}`}
                                                        {agent.state && `, ${agent.state}`}
                                                        {agent.zipCode && ` ${agent.zipCode}`}
                                                    </>
                                                )}
                                            </Text>
                                        </Box>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </VStack>
            </Box>
        );
    };

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
            <DrawerOverlay />
            <DrawerContent bg={bgColor}>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px" borderColor={cardBorder} pb={2} pt={3} pr={12}>
                    <VStack align="stretch" spacing={1.5}>
                        {/* Top Row - Borrower Name & Actions */}
                        <HStack justifyContent="space-between" alignItems="center">
                            <Box flex="1">
                                <HStack spacing={1.5} align="center" flexWrap="wrap">
                                    <Text color={headerColor} fontSize="lg" fontWeight="bold" lineHeight="1">
                                        {loan?.borrower}
                                    </Text>
                                    <Badge colorScheme="blue" variant="subtle" fontSize="xs" px={1.5} py={0.5}>
                                        {uwmData?.loanSummary?.status || 'Loading...'}
                                    </Badge>
                                    <Text fontSize="xs" color={subtextColor} fontFamily="mono">
                                        {loan?.id}
                                    </Text>
                                </HStack>
                            </Box>
                            <HStack spacing={2}>
                                {uwmData?.easeUrl && (
                                    <Button
                                        as={Link}
                                        href={uwmData.easeUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        leftIcon={<Icon as={ExternalLink} boxSize={3} />}
                                        size="xs"
                                        colorScheme="blue"
                                        variant="solid"
                                        fontSize="xs"
                                        _hover={{ textDecoration: 'none' }}
                                    >
                                        Open in UWM
                                    </Button>
                                )}
                                <IconButton
                                    aria-label="Refresh Data"
                                    icon={<Icon as={RotateCcw} />}
                                    onClick={fetchUwmData}
                                    isLoading={isLoading}
                                    size="xs"
                                    variant="ghost"
                                />
                            </HStack>
                        </HStack>

                        {/* Key Information Grid - Clean Table Format */}
                        <Box 
                            borderWidth="1px" 
                            borderColor={cardBorder} 
                            borderRadius="md" 
                            overflow="hidden"
                            bg={cardBg}
                        >
                            <SimpleGrid columns={{ base: 2, md: 3 }} gap={0}>
                                {/* Row 1 */}
                                {/* Purpose */}
                                <Box px={2} py={1.5} borderRightWidth={{ base: "1px", md: "1px" }} borderBottomWidth="1px" borderColor={cardBorder}>
                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold" mb={0.5}>
                                        Purpose
                                    </Text>
                                    <Skeleton isLoaded={!isLoading} h={isLoading ? "14px" : "auto"} borderRadius="sm">
                                        <Text fontSize="xs" fontWeight="medium" color={headerColor} noOfLines={2} lineHeight="1.2">
                                            {uwmData?.loanSummary?.loanPurpose || 'N/A'}
                                        </Text>
                                    </Skeleton>
                                </Box>

                                {/* Property Address */}
                                <Box px={2} py={1.5} borderRightWidth={{ base: "0px", md: "1px" }} borderBottomWidth="1px" borderColor={cardBorder}>
                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold" mb={0.5}>
                                        Property
                                    </Text>
                                    <Skeleton isLoaded={!isLoading} h={isLoading ? "14px" : "auto"} borderRadius="sm">
                                        <Text fontSize="xs" fontWeight="medium" color={headerColor} noOfLines={2} lineHeight="1.2">
                                            {uwmData?.loanSummary?.propertyAddress || 'N/A'}
                                        </Text>
                                    </Skeleton>
                                </Box>

                                {/* Rate */}
                                <Box px={2} py={1.5} borderRightWidth="1px" borderBottomWidth="1px" borderColor={cardBorder}>
                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold" mb={0.5}>
                                        Rate
                                    </Text>
                                    <Skeleton isLoaded={!isLoading} h={isLoading ? "14px" : "auto"} borderRadius="sm">
                                        <Text fontSize="md" fontWeight="bold" color="green.600" lineHeight="1.2">
                                            {uwmData?.financials?.interestRate || 'N/A'}
                                        </Text>
                                    </Skeleton>
                                </Box>

                                {/* Row 2 */}
                                {/* Loan Officer */}
                                <Box px={2} py={1.5} borderRightWidth={{ base: "1px", md: "1px" }} borderBottomWidth={{ base: "1px", md: "0px" }} borderColor={cardBorder}>
                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold" mb={0.5}>
                                        Loan Officer
                                    </Text>
                                    <Skeleton isLoaded={!isLoading} h={isLoading ? "14px" : "auto"} borderRadius="sm">
                                        <Text fontSize="xs" fontWeight="medium" color={headerColor} noOfLines={2} lineHeight="1.2">
                                            {uwmData?.team?.loanOfficer || 'N/A'}
                                        </Text>
                                    </Skeleton>
                                </Box>

                                {/* Product */}
                                <Box px={2} py={1.5} borderRightWidth={{ base: "0px", md: "1px" }} borderBottomWidth={{ base: "1px", md: "0px" }} borderColor={cardBorder}>
                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold" mb={0.5}>
                                        Product
                                    </Text>
                                    <Skeleton isLoaded={!isLoading} h={isLoading ? "14px" : "auto"} borderRadius="sm">
                                        <Text fontSize="xs" fontWeight="medium" color={headerColor} noOfLines={2} lineHeight="1.2">
                                            {uwmData?.loanSummary?.product || 'N/A'}
                                        </Text>
                                    </Skeleton>
                                </Box>

                                {/* Property Type */}
                                <Box px={2} py={1.5} borderColor={cardBorder}>
                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold" mb={0.5}>
                                        Property Type
                                    </Text>
                                    <Skeleton isLoaded={!isLoading} h={isLoading ? "14px" : "auto"} borderRadius="sm">
                                        <Text fontSize="xs" fontWeight="medium" color={headerColor} noOfLines={2} lineHeight="1.2">
                                            {uwmData?.loanSummary?.propertyType || 'N/A'}
                                        </Text>
                                    </Skeleton>
                                </Box>
                            </SimpleGrid>
                        </Box>

                        {/* Closing Date & Lock Expiration Row */}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                            {/* Closing Date */}
                            <Box 
                                borderWidth="1px" 
                                borderColor={cardBorder}
                                borderRadius="md" 
                                overflow="hidden"
                                bg={cardBg}
                            >
                                <HStack 
                                    justify="space-between" 
                                    align="center"
                                    px={2}
                                    py={1.5}
                                >
                                    <HStack spacing={1.5}>
                                        <Icon 
                                            as={Calendar} 
                                            boxSize={3.5} 
                                            color="green.500" 
                                        />
                                        <Text fontSize="xs" color={headerColor} fontWeight="semibold">
                                            Closing Date
                                        </Text>
                                    </HStack>
                                    <Skeleton isLoaded={!isLoading} borderRadius="md">
                                        <Text 
                                            fontSize="xs" 
                                            fontWeight="bold" 
                                            color={headerColor}
                                        >
                                            {(() => {
                                                const closingDate = uwmData?.importantDates?.find(d => d.name === 'Closing Date')?.date;
                                                const estClosing = uwmData?.milestones?.estimatedClosing;
                                                return closingDate && closingDate !== 'N/A' ? closingDate : estClosing || 'N/A';
                                            })()}
                                        </Text>
                                    </Skeleton>
                                </HStack>
                            </Box>

                            {/* Lock Expiration - Always Show */}
                            <Box 
                                borderWidth="1px" 
                                borderColor={uwmData?.rateLock?.isExpired ? rateLockBorderExpired : rateLockBorderNormal}
                                borderRadius="md" 
                                overflow="hidden"
                                bg={uwmData?.rateLock?.isExpired ? rateLockBgExpired : rateLockBgNormal}
                            >
                                <HStack 
                                    justify="space-between" 
                                    align="center"
                                    px={2}
                                    py={1.5}
                                >
                                    <HStack spacing={1.5}>
                                        <Icon 
                                            as={Lock} 
                                            boxSize={3.5} 
                                            color={uwmData?.rateLock?.isExpired ? 'red.500' : uwmData?.rateLock?.lockStatus === "Locked" ? 'blue.500' : 'gray.400'} 
                                        />
                                        <Text fontSize="xs" color={headerColor} fontWeight="semibold">
                                            Rate Lock
                                        </Text>
                                    </HStack>
                                    <Skeleton isLoaded={!isLoading} borderRadius="md">
                                        <HStack spacing={1.5}>
                                            {uwmData?.rateLock?.isExpired && (
                                                <Badge colorScheme="red" fontSize="xs" px={1.5} py={0.5}>EXPIRED</Badge>
                                            )}
                                            {uwmData?.rateLock?.lockStatus === "Locked" ? (
                                                <Text 
                                                    fontSize="xs" 
                                                    fontWeight="bold" 
                                                    color={uwmData?.rateLock?.isExpired ? 'red.600' : headerColor}
                                                >
                                                    {uwmData?.rateLock?.lockExpiration || 'N/A'}
                                                </Text>
                                            ) : (
                                                <Badge colorScheme="gray" fontSize="xs" px={1.5} py={0.5}>
                                                    Not Locked
                                                </Badge>
                                            )}
                                        </HStack>
                                    </Skeleton>
                                </HStack>
                            </Box>
                        </SimpleGrid>
                    </VStack>
                </DrawerHeader>

                <DrawerBody>
                    {error && (
                        <Alert status="error" borderRadius="md" mt={4}>
                            <AlertIcon />
                            {error}
                        </Alert>
                    )}
                    <VStack spacing={4} align="stretch" py={4}>
                        {/* Row 1: Borrower & Co-Borrower */}
                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                            <InfoCard 
                                title="Borrower" 
                                data={uwmData?.borrower} 
                                isLoading={isLoading}
                                expectedKeys={['name', 'email', 'phone']}
                            />
                            <InfoCard 
                                title="Co-Borrower" 
                                data={uwmData?.coBorrower} 
                                isLoading={isLoading}
                                expectedKeys={['name']}
                            />
                        </SimpleGrid>

                        {/* Row 2: Key Metrics - 4 Column Grid */}
                        <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4}>
                            <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder} textAlign="center">
                                <Text fontSize="xs" color={subtextColor} mb={1}>FICO</Text>
                                <Skeleton isLoaded={!isLoading} h="24px" borderRadius="md">
                                    <Text fontSize="lg" fontWeight="bold" color="green.600">
                                        {uwmData?.financials?.fico || 'N/A'}
                                    </Text>
                                </Skeleton>
                            </Box>
                            <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder} textAlign="center">
                                <Text fontSize="xs" color={subtextColor} mb={1}>Rate</Text>
                                <Skeleton isLoaded={!isLoading} h="24px" borderRadius="md">
                                    <Text fontSize="lg" fontWeight="bold" color={headerColor}>
                                        {uwmData?.financials?.interestRate || 'N/A'}
                                    </Text>
                                </Skeleton>
                            </Box>
                            <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder} textAlign="center">
                                <Text fontSize="xs" color={subtextColor} mb={1}>LTV</Text>
                                <Skeleton isLoaded={!isLoading} h="24px" borderRadius="md">
                                    <Text fontSize="lg" fontWeight="bold" color={headerColor}>
                                        {uwmData?.financials?.ltv || 'N/A'}
                                    </Text>
                                </Skeleton>
                            </Box>
                            <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder} textAlign="center">
                                <Text fontSize="xs" color={subtextColor} mb={1}>DTI</Text>
                                <Skeleton isLoaded={!isLoading} h="24px" borderRadius="md">
                                    <Text fontSize="lg" fontWeight="bold" color={headerColor}>
                                        {uwmData?.financials?.dtiBackEnd || 'N/A'}
                                    </Text>
                                </Skeleton>
                            </Box>
                        </SimpleGrid>

                        {/* Row 3: Loan Details & Loan Financials */}
                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                            {/* Loan Details */}
                            <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder}>
                                <HStack mb={3}>
                                    <Icon as={TrendingUp} boxSize={5} color="cyan.500" />
                                    <Text fontSize="md" fontWeight="bold" color={headerColor}>Loan Details</Text>
                                </HStack>
                                {isLoading ? (
                                    <SkeletonText noOfLines={6} spacing="3" skeletonHeight="2" />
                                ) : (
                                    <VStack align="stretch" spacing={2}>
                                        {uwmData?.loanSummary?.loanType && <DataRow label="Loan Type" value={uwmData.loanSummary.loanType} />}
                                        {uwmData?.loanSummary?.loanProgram && <DataRow label="Program" value={uwmData.loanSummary.loanProgram} />}
                                        {uwmData?.loanSummary?.product && <DataRow label="Product" value={uwmData.loanSummary.product} />}
                                        {uwmData?.loanSummary?.propertyType && <DataRow label="Property Type" value={uwmData.loanSummary.propertyType} />}
                                        {uwmData?.loanSummary?.occupancyType && <DataRow label="Occupancy" value={uwmData.loanSummary.occupancyType} />}
                                        {uwmData?.loanSummary?.aus && <DataRow label="AUS" value={uwmData.loanSummary.aus} />}
                                        {uwmData?.loanSummary?.investor && <DataRow label="Investor" value={uwmData.loanSummary.investor} />}
                                    </VStack>
                                )}
                            </Box>

                            {/* Loan Financials */}
                            <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder}>
                                <HStack mb={3}>
                                    <Icon as={DollarSign} boxSize={5} color="green.500" />
                                    <Text fontSize="md" fontWeight="bold" color={headerColor}>Loan Financials</Text>
                                </HStack>
                                {isLoading ? (
                                    <SkeletonText noOfLines={8} spacing="3" skeletonHeight="2" />
                                ) : (
                                    <VStack align="stretch" spacing={2}>
                                        {uwmData?.financials?.baseLoanAmount && <DataRow label="Base Loan Amount" value={uwmData.financials.baseLoanAmount} />}
                                        {uwmData?.financials?.dtiFrontEnd && <DataRow label="DTI Front-End" value={uwmData.financials.dtiFrontEnd} />}
                                        {uwmData?.financials?.piti && <DataRow label="PITI" value={uwmData.financials.piti} />}
                                        {uwmData?.financials?.monthlyMI && <DataRow label="Monthly MI" value={uwmData.financials.monthlyMI} />}
                                        {uwmData?.financials?.discountPoints && <DataRow label="Discount Points" value={uwmData.financials.discountPoints} />}
                                        {uwmData?.financials?.lenderCredits && <DataRow label="Lender Credits" value={uwmData.financials.lenderCredits} />}
                                        {(uwmData?.financials?.appraisedValue || uwmData?.financials?.salesPrice) && <Divider my={1} />}
                                        {uwmData?.financials?.appraisedValue && <DataRow label="Appraised Value" value={uwmData.financials.appraisedValue} valueColor="green.600" />}
                                        {uwmData?.financials?.salesPrice && <DataRow label="Sales Price" value={uwmData.financials.salesPrice} />}
                                    </VStack>
                                )}
                            </Box>
                        </SimpleGrid>

                        {/* Row 4: Important Dates & Rate Lock */}
                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                            {/* Important Dates */}
                            <Box p={5} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder}>
                                <Text fontSize="lg" fontWeight="bold" mb={3} color={headerColor}>Important Dates</Text>
                                <VStack align="stretch" spacing={3}>
                                    {isLoading && (
                                        <>
                                            <Skeleton h="20px" borderRadius="md" /><Skeleton h="20px" borderRadius="md" /><Skeleton h="20px" borderRadius="md" />
                                        </>
                                    )}
                                    {!isLoading && uwmData?.importantDates?.map(item => {
                                        // Replace Closing Date with Est. Closing if needed
                                        if (item.name === 'Closing Date' && (!item.date || item.date === 'N/A')) {
                                            const estClosing = uwmData?.milestones?.estimatedClosing;
                                            if (estClosing && estClosing !== 'N/A') {
                                                return (
                                                    <HStack key="est-closing" justify="space-between">
                                                        <Text fontSize="sm" color={subtextColor}>Est. Closing</Text>
                                                        <Badge colorScheme="blue" variant="subtle" px={2} py={0.5} borderRadius="md" fontSize="xs">{estClosing}</Badge>
                                                    </HStack>
                                                );
                                            }
                                            return null;
                                        }
                                        return (
                                            <HStack key={item.name} justify="space-between">
                                                <Text fontSize="sm" color={subtextColor}>{item.name}</Text>
                                                <Badge colorScheme="blue" variant="subtle" px={2} py={0.5} borderRadius="md" fontSize="xs">{item.date}</Badge>
                                            </HStack>
                                        );
                                    })}
                                     {!isLoading && !uwmData?.importantDates?.length && <Text fontSize="sm" color={subtextColor}>No dates available.</Text>}
                                </VStack>
                            </Box>

                            {/* Rate Lock */}
                            {(uwmData?.rateLock?.lockDate || uwmData?.rateLock?.lockStatus || isLoading) && (
                                <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder}>
                                    <HStack mb={3}>
                                        <Icon as={Lock} boxSize={5} color="blue.500" />
                                        <Text fontSize="md" fontWeight="bold" color={headerColor}>Rate Lock</Text>
                                    </HStack>
                                    {isLoading ? (
                                        <SkeletonText noOfLines={3} spacing="3" skeletonHeight="2" />
                                    ) : (
                                        <VStack align="stretch" spacing={2}>
                                            {uwmData.rateLock.lockStatus && (
                                                <HStack justify="space-between">
                                                    <Text fontSize="sm" color={subtextColor}>Status</Text>
                                                    <StatusBadge 
                                                        isActive={uwmData.rateLock.lockStatus === "Locked"} 
                                                        activeText="Locked" 
                                                        inactiveText="Unlocked" 
                                                    />
                                                </HStack>
                                            )}
                                            {uwmData.rateLock.isExpired && (
                                                <Alert status="warning" size="sm" borderRadius="md" py={1}>
                                                    <AlertIcon boxSize={3} />
                                                    <Text fontSize="xs">Lock Expired</Text>
                                                </Alert>
                                            )}
                                            {uwmData.rateLock.lockDate && <DataRow label="Lock Date" value={uwmData.rateLock.lockDate} />}
                                            {uwmData.rateLock.lockExpiration && <DataRow label="Expiration" value={uwmData.rateLock.lockExpiration} />}
                                            {uwmData.rateLock.firstExtension && <DataRow label="1st Extension" value={uwmData.rateLock.firstExtension} />}
                                            {uwmData.rateLock.secondExtension && <DataRow label="2nd Extension" value={uwmData.rateLock.secondExtension} />}
                                        </VStack>
                                    )}
                                </Box>
                            )}
                        </SimpleGrid>

                        {/* Row 5: UWM Team & Documents */}
                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                            {/* UWM Team */}
                            {(uwmData?.team?.loanOfficer || uwmData?.team?.processor?.name || uwmData?.team?.seniorUnderwriter?.name || isLoading) && (
                                <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder}>
                                    <HStack mb={3}>
                                        <Icon as={Users} boxSize={5} color="teal.500" />
                                        <Text fontSize="md" fontWeight="bold" color={headerColor}>UWM Team</Text>
                                    </HStack>
                                    {isLoading ? (
                                        <SkeletonText noOfLines={5} spacing="3" skeletonHeight="2" />
                                    ) : (
                                        <VStack align="stretch" spacing={3}>
                                            {uwmData.team.loanOfficer && (
                                                <Box>
                                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold">Loan Officer</Text>
                                                    <Text fontSize="xs" fontWeight="medium" color={headerColor}>{uwmData.team.loanOfficer}</Text>
                                                </Box>
                                            )}
                                            {uwmData.team.processor?.name && (
                                                <Box>
                                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold">Processor</Text>
                                                    <Text fontSize="xs" fontWeight="medium" color={headerColor}>{uwmData.team.processor.name}</Text>
                                                    {uwmData.team.processor.email && (
                                                        <Link href={`mailto:${uwmData.team.processor.email}`} fontSize="xs" color="blue.500">
                                                            {uwmData.team.processor.email}
                                                        </Link>
                                                    )}
                                                </Box>
                                            )}
                                            {uwmData.team.seniorUnderwriter?.name && (
                                                <Box>
                                                    <Text fontSize="xs" color={subtextColor} fontWeight="semibold">Sr. Underwriter</Text>
                                                    <Text fontSize="xs" fontWeight="medium" color={headerColor}>{uwmData.team.seniorUnderwriter.name}</Text>
                                                    {uwmData.team.seniorUnderwriter.email && (
                                                        <Link href={`mailto:${uwmData.team.seniorUnderwriter.email}`} fontSize="xs" color="blue.500">
                                                            {uwmData.team.seniorUnderwriter.email}
                                                        </Link>
                                                    )}
                                                </Box>
                                            )}
                                        </VStack>
                                    )}
                                </Box>
                            )}

                            {/* Documents */}
                            {(uwmData?.documents?.cdSigned !== undefined || uwmData?.documents?.eSignCompleted !== undefined || isLoading) && (
                                <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={cardBorder}>
                                    <HStack mb={3}>
                                        <Icon as={FileText} boxSize={5} color="orange.500" />
                                        <Text fontSize="md" fontWeight="bold" color={headerColor}>Documents</Text>
                                    </HStack>
                                    {isLoading ? (
                                        <SkeletonText noOfLines={6} spacing="3" skeletonHeight="2" />
                                    ) : (
                                        <VStack align="stretch" spacing={2}>
                                            {uwmData.documents.cdSigned !== undefined && (
                                                <HStack justify="space-between">
                                                    <Text fontSize="sm" color={subtextColor}>CD Signed</Text>
                                                    <HStack spacing={1}>
                                                        <StatusBadge isActive={uwmData.documents.cdSigned} activeText="Yes" inactiveText="No" />
                                                        {uwmData.documents.cdSignedDate && <Text fontSize="xs" color={subtextColor}>({uwmData.documents.cdSignedDate})</Text>}
                                                    </HStack>
                                                </HStack>
                                            )}
                                            {uwmData.documents.eSignCompleted !== undefined && (
                                                <HStack justify="space-between">
                                                    <Text fontSize="sm" color={subtextColor}>E-Sign Done</Text>
                                                    <StatusBadge isActive={uwmData.documents.eSignCompleted} activeText="Yes" inactiveText="No" />
                                                </HStack>
                                            )}
                                            {uwmData.documents.initialLoanEstimate && <DataRow label="Initial LE" value={uwmData.documents.initialLoanEstimate} />}
                                            {uwmData.documents.mostRecentLE && <DataRow label="Recent LE" value={uwmData.documents.mostRecentLE} />}
                                            {uwmData.documents.initialCD && <DataRow label="Initial CD" value={uwmData.documents.initialCD} />}
                                            {uwmData.documents.mostRecentCD && <DataRow label="Recent CD" value={uwmData.documents.mostRecentCD} />}
                                        </VStack>
                                    )}
                                </Box>
                            )}
                        </SimpleGrid>

                        {/* Row 6: Conditions - Full Width Accordion */}
                        <Accordion allowToggle>
                            <AccordionItem borderWidth="1px" borderRadius="lg" borderColor={cardBorder} bg={cardBg}>
                                <h2>
                                    <AccordionButton _expanded={{ bg: accordionButtonBg }}>
                                        <HStack flex="1" textAlign="left" spacing={2}>
                                            <Icon as={FileText} boxSize={5} color="red.500" />
                                            <Text fontWeight="bold" fontSize="md" color={headerColor}>Conditions</Text>
                                            <Badge colorScheme="red" fontSize="xs">
                                                {isLoading ? '...' : (uwmData?.conditions?.groups || []).reduce((sum, group) => sum + (group?.conditions?.length || 0), 0)}
                                            </Badge>
                                        </HStack>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4}>
                                    <ConditionsCard conditions={uwmData?.conditions} isLoading={isLoading} />
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>

                        {/* Row 7: Realtor Information - Only if NOT Refinance */}
                        {uwmData?.loanSummary?.loanPurpose && 
                         uwmData.loanSummary.loanPurpose.toLowerCase() !== 'refinance' && 
                         (isLoading || uwmData?.realtorInfo?.buyerAgent || uwmData?.realtorInfo?.sellerAgent) && (
                            <Accordion allowToggle>
                                <AccordionItem borderWidth="1px" borderRadius="lg" borderColor={cardBorder} bg={cardBg}>
                                    <h2>
                                        <AccordionButton _expanded={{ bg: accordionButtonBg }}>
                                            <HStack flex="1" textAlign="left" spacing={2}>
                                                <Icon as={Building2} boxSize={5} color="purple.500" />
                                                <Text fontWeight="bold" fontSize="md" color={headerColor}>Realtor Information</Text>
                                            </HStack>
                                            <AccordionIcon />
                                        </AccordionButton>
                                    </h2>
                                    <AccordionPanel pb={4}>
                                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                                            <RealtorCard 
                                                title="Buyer's Agent" 
                                                agent={uwmData?.realtorInfo?.buyerAgent}
                                                icon={User}
                                                colorScheme="green"
                                                isLoading={isLoading}
                                            />
                                            <RealtorCard 
                                                title="Seller's Agent" 
                                                agent={uwmData?.realtorInfo?.sellerAgent}
                                                icon={User}
                                                colorScheme="orange"
                                                isLoading={isLoading}
                                            />
                                        </SimpleGrid>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Accordion>
                        )}

                        {/* SuperAdmin Debug Section */}
                        {isSuperAdmin && (
                            <>
                                <Box p={3} bg="blue.50" borderRadius="md" mt={2}>
                                    <Text fontSize="xs" fontWeight="bold" mb={2}>Debug Info:</Text>
                                    <VStack align="start" spacing={1} fontSize="xs">
                                        <Text>SuperAdmin: {isSuperAdmin ? '✅' : '❌'} | Raw Data: {rawData ? '✅' : '❌'} | Loading: {isLoading ? '⏳' : '✅'}</Text>
                                    </VStack>
                                </Box>

                                {rawData && !isLoading && (
                                    <Accordion allowToggle>
                                        <AccordionItem borderWidth="1px" borderRadius="lg" borderColor={cardBorder}>
                                            <h2>
                                                <AccordionButton _expanded={{ bg: accordionButtonBg }}>
                                                    <Box flex="1" textAlign="left" fontWeight="bold" fontSize="sm">
                                                        🔧 Raw API Response
                                                    </Box>
                                                    <AccordionIcon />
                                                </AccordionButton>
                                            </h2>
                                            <AccordionPanel pb={4}>
                                                <Tabs variant="enclosed-colored" size="sm">
                                                    <TabList>
                                                        <Tab>Insights</Tab>
                                                        <Tab>Conditions</Tab>
                                                    </TabList>
                                                    <TabPanels>
                                                        <TabPanel>
                                                            <Code as="pre" p={4} w="100%" borderRadius="md" overflowX="auto" bg={codeBg} fontSize="xs">
                                                                {JSON.stringify(rawData.insights, null, 2)}
                                                            </Code>
                                                        </TabPanel>
                                                        <TabPanel>
                                                            <Code as="pre" p={4} w="100%" borderRadius="md" overflowX="auto" bg={codeBg} fontSize="xs">
                                                                {JSON.stringify(rawData.conditions, null, 2)}
                                                            </Code>
                                                        </TabPanel>
                                                    </TabPanels>
                                                </Tabs>
                                            </AccordionPanel>
                                        </AccordionItem>
                                    </Accordion>
                                )}
                            </>
                        )}
                    </VStack>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};

export default UwmLoanDetailsDrawer;
